import { fetchWithHeaders } from '@sendible/common';
import { EVENTS, subscribe } from '@sendible/event-manager';
import { MediaIdType, MediaType } from '@sendible/shared-state-bridge';
import endpoints from '../../data-layer/endpoints';
import { urlUploadSuccess } from '../../pages/MediaLibrary/pendoEvents';
import { getUrlWithCredentialsFromUser } from '../../utils';
import { MediaFileType } from '../medias/types';

// The create-media call only registers the upload with the backend, which then fetches and
// processes the URL asynchronously - it says nothing about the media actually being done.
// Used to make the concurrency-limited worker pool below wait for a real Ready/Failed instead
// of freeing its slot the moment that registration call returns, which is fast enough that
// every queued row would otherwise get dispatched to the backend in a single rapid burst
// regardless of concurrencyLimit.
const waitForMediaSettled = (mediaId: MediaIdType) =>
  new Promise<void>((resolve) => {
    const unsubscribeReady = subscribe(EVENTS.UPLOAD_READY, (media: MediaType) => {
      if (media.id !== mediaId) return;

      unsubscribeReady();
      unsubscribeFailed();
      resolve();
    });
    const unsubscribeFailed = subscribe(EVENTS.UPLOAD_FAILED, ({ mediaId: failedMediaId }: { mediaId?: MediaIdType }) => {
      if (failedMediaId !== mediaId) return;

      unsubscribeReady();
      unsubscribeFailed();
      resolve();
    });
  });

export const useUploadUrls = ({ addToUploadingMedia, user, onError }: UseUploadUrlsParam) => {
  const { method, endpoint } = endpoints.UploadMedias;
  const { method: mediaMethod, endpoint: mediaEndpoint } = endpoints.CreateMedia;

  const uploadUrl = async (url: string, fileId: string, onMediaCreated: (args: OnMediaCreatedUrlsArgsType) => void, waitUntilSettled?: boolean) => {
    const body: MediaUploadBodyType = {
      source: {
        type: 'Url',
        url,
      },
    };

    body.initiator = 'ComposeBox';

    const uploadIntentResponse = (await fetchWithHeaders({
      method,
      url: getUrlWithCredentialsFromUser(endpoint, user),
      body,
      params: { dashboardId: user.userId },
    })) as { result: MediaUploadIntentType };
    const { result: uploadIntent } = uploadIntentResponse;

    const mediaResponse = (await fetchWithHeaders({
      method: mediaMethod,
      url: getUrlWithCredentialsFromUser(mediaEndpoint, user),
      body: {
        name: uploadIntent.fileName,
        components: {
          original: uploadIntent.id,
        },
      },
      params: { dashboardId: user.userId },
    })) as { result: MediaType };
    const { result: media } = mediaResponse;

    // Subscribe before onMediaCreated/addToUploadingMedia - both can synchronously trigger a
    // cancellation that publishes UPLOAD_FAILED for this exact media id (see
    // BulkImportUploadBridge), which must not be missed.
    const settled = waitUntilSettled ? waitForMediaSettled(media.id) : undefined;

    onMediaCreated({ url, fileId, mediaId: media.id, mediaType: MediaFileType.Unspecified, name: media.name });

    addToUploadingMedia(media);

    window.pendo.track(urlUploadSuccess, { url, mediaId: media.id });

    if (settled) {
      await settled;
    }
  };

  return async ({ files, onUploadStarted, onMediaCreated, concurrencyLimit, signal }: UploadUrlsArgs) => {
    if (!files.length) return;

    // Register every file up front, regardless of the concurrency limit, so all of them
    // show up in the tray straight away rather than trickling in as earlier ones finish.
    const fileIds = files.map((file) => onUploadStarted(file));

    const runUpload = async (index: number, waitUntilSettled?: boolean) => {
      const file = files[index];
      const fileId = fileIds[index];

      try {
        await uploadUrl(file.url, fileId, onMediaCreated, waitUntilSettled);
      } catch (error) {
        onError(error, { url: file.url, fileId });
      }
    };

    if (!concurrencyLimit || concurrencyLimit >= files.length) {
      await Promise.all(files.map((_file, index) => runUpload(index)));

      return;
    }

    // A fixed-size pool of workers, each pulling the next queued index only once its previous
    // upload has actually finished processing (Ready/Failed) - not merely once the backend has
    // acknowledged the upload request, which returns quickly and would otherwise let every
    // queued file get dispatched to the backend in a single burst regardless of this limit.
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < files.length && !signal?.aborted) {
        const index = nextIndex;

        nextIndex += 1;
        await runUpload(index, true);
      }
    };

    await Promise.all(Array.from({ length: concurrencyLimit }, worker));
  };
};
